import React, { RefObject, useEffect } from 'react';
import L, { LatLng } from 'leaflet';
import { Map, Marker, Polyline, TileLayer } from 'react-leaflet';

import { rankColors } from 'views/components/DrivingControl/RankIcon';
import { IMapSettings, IMapSettingsParams } from '../../Video/MapComponent';
import { ParsedRoute, ParsedStop, AlternativeStop } from 'types/map';
import { ParsedMapEvent } from 'utils/events';

import {
  eventClusterIcon,
  eventIcon,
  stopIcon,
  correspondenceIcon,
} from './Icons';

type Props = {
  onChangeMapSettings: (params: IMapSettingsParams) => void;
  mapSettings: IMapSettings;
  routes?: ParsedRoute[];
  stops?: ParsedStop[];
  events?: ParsedMapEvent[];
  onDragEnd?: (event: L.DragEndEvent) => void;
  onChangeZoom?: (event: L.LeafletEvent) => void;
  onClickEvent?: (data: any) => void;
  children?: React.ReactNode;
  mapRef?: RefObject<Map>;
};

export const MapComponent = React.forwardRef<Map, Props>(
  (
    {
      stops,
      routes,
      children,
      mapSettings,
      events,
      onChangeMapSettings,
      onChangeZoom,
      onDragEnd,
      onClickEvent,
      mapRef,
    },
    ref
  ) => {
    let mapStops: React.ReactNode[] = [];
    if (stops) {
      mapStops = stops.map((stop: ParsedStop) => (
        <Marker icon={stopIcon()} position={stop.coords} key={stop.id} />
      ));
    }

    let mapRoutes: React.ReactNode[] = [];
    if (routes) {
      mapRoutes = routes.map((route) => (
        <Polyline
          color={route.color || 'blue'}
          key={route.id}
          positions={route.coords}
        />
      ));
    }

    const onChangeBounds = (event: L.LeafletEvent | L.DragEndEvent) => {
      const bounds = event.target.getBounds();
      onChangeMapSettings({
        bounds: [bounds.getSouthWest(), bounds.getNorthEast()],
      });
    };

    useEffect(() => {
      const bounds = mapRef?.current?.leafletElement.getBounds();
      if (bounds) {
        onChangeMapSettings({
          bounds: [bounds.getSouthWest(), bounds.getNorthEast()],
        });
      }
    }, [mapRef, onChangeMapSettings]);

    return (
      <Map
        preferCanvas
        center={mapSettings.center}
        zoom={mapSettings.zoom}
        zoomControl={false}
        ondragend={(event: L.DragEndEvent) => {
          onChangeBounds(event);
          onChangeMapSettings({
            zoom: event.target.getZoom(),
            center: event.target.getCenter(),
          });
          if (onDragEnd) {
            onDragEnd(event);
          }
        }}
        onzoomend={(event: L.LeafletEvent) => {
          onChangeMapSettings({
            zoom: event.target.getZoom(),
            center: event.target.getCenter(),
          });
          onChangeBounds(event);
          if (onChangeZoom) {
            onChangeZoom(event);
          }
        }}
        doubleClickZoom={false}
        ref={ref}
      >
        <TileLayer url={process.env.REACT_APP_MAP_TILES as string} />
        {children}
        {mapRoutes}
        {mapStops}
        {events &&
          events.map(({ grades, type, coords, routePosition, data }) =>
            type === 'route' ? (
              <Polyline
                onclick={() => {
                  onClickEvent && onClickEvent(data);
                }}
                positions={routePosition!}
                color={rankColors[grades as number]}
                weight={6}
              />
            ) : (
              <Marker
                position={coords}
                icon={((eventType: 'event' | 'group' | 'route') => {
                  switch (eventType) {
                    case 'group':
                      return eventClusterIcon(grades as {});
                    case 'event':
                      return eventIcon(grades as number);
                  }
                })(type)}
                onclick={
                  type === 'group'
                    ? () =>
                        onChangeMapSettings({
                          center: coords,
                          zoom: mapSettings.zoom + 2,
                        })
                    : () => {
                        onClickEvent && onClickEvent(data);
                      }
                }
              />
            )
          )}
      </Map>
    );
  }
);

export default MapComponent;
